import type {
  TradevestGuidanceActor,
  TradevestGuidanceTimingBasis,
  TradevestReminderTemplate,
  TradevestSupportGuidance,
} from '@mygate/common';

export type GuidanceControl =
  | { kind: 'remind'; label: string; template: TradevestReminderTemplate }
  | { kind: 'copyRequest'; label: string }
  | { kind: 'note'; text: string }
  | { kind: 'none' };

export const WAITING_ON_LABELS: Record<TradevestGuidanceActor, string> = {
  CUSTOMER: 'Kunde',
  TRADEVEST: 'Tradevest',
  SYSTEM: 'System',
  ADMIN: 'Admin',
};

const BASIS_LABELS: Record<TradevestGuidanceTimingBasis, string> = {
  BANK_DETAILS_SUBMITTED: 'Bankdaten eingereicht',
  TERMS_ACCEPTED: 'Bedingungen akzeptiert',
  ONBOARDING_SUBMITTED: 'Onboarding eingereicht',
  PRODUCTS_CREATED: 'Produkte angelegt',
};

/**
 * Wording approved by Albert Hild on 2026-09-28; change it only with a new approval. Du, "investieren" /
 * "Investmentumgebung", never "Trading". Nothing is sent automatically; the agent reviews the prefilled push and
 * presses send.
 */
export const REMINDER_TEMPLATES: Record<TradevestReminderTemplate, { title: string; body: string }> = {
  BANK_DETAILS: {
    title: 'Deine Investmentumgebung wartet',
    body: 'Für Deine Investmentumgebung fehlt noch Dein Referenzkonto. Öffne die App und hinterlege es, um die Einrichtung fortzusetzen.',
  },
  ACCEPT_TERMS: {
    title: 'Nur noch ein Schritt',
    body: 'Bitte bestätige in der App die Bedingungen für Deine Investmentumgebung. Danach geht es automatisch weiter.',
  },
  BANK_DETAILS_INVALID: {
    title: 'Bitte prüfe Dein Referenzkonto',
    body: 'Die angegebenen Bankdaten konnten nicht bestätigt werden. Bitte gib Dein Referenzkonto in der App erneut ein.',
  },
  TAX_ID: {
    title: 'Steuer-ID für Deine Investmentumgebung',
    body: 'Für Dein Wohnsitzland brauchen wir noch Deine Steuer-Identifikationsnummer. Öffne die App und trage sie ein, danach geht es automatisch weiter.',
  },
};

export function resolveGuidanceControl(guidance: TradevestSupportGuidance, isAdmin: boolean): GuidanceControl {
  switch (guidance.action) {
    case 'REMIND_CUSTOMER':
      return guidance.reminder
        ? { kind: 'remind', label: 'Kunde erinnern', template: guidance.reminder }
        : { kind: 'none' };
    case 'ASK_TRADEVEST':
      return { kind: 'copyRequest', label: 'Anfrage an Tradevest kopieren' };
    case 'ADMIN_RETRY':
      // Admins already have the existing Retry button, highlighted (spec §3.1) — no second control here.
      return isAdmin ? { kind: 'none' } : { kind: 'note', text: 'Ein Admin muss Retry auslösen' };
    case 'ADMIN_DIAGNOSE':
      // Admins already have the existing "View Onboarding Log" button — no second control here.
      return isAdmin ? { kind: 'none' } : { kind: 'note', text: 'An einen Admin geben' };
    case 'WAIT':
      return { kind: 'none' };
  }
}

export function formatGuidanceTiming(
  guidance: TradevestSupportGuidance,
  formatDate: (iso: string) => string,
): string | null {
  // The explanation already opens with "Zeitpunkt unbekannt" for this case (getTradevestSupportGuidance),
  // so repeating it on the timing line would say it twice.
  if (guidance.timingUnknown) return null;
  const { timing } = guidance;
  if (!timing) return null;
  const head = `seit ${Math.floor(timing.ageHours)} h (${BASIS_LABELS[timing.basis]}) · normal bis ${timing.expectedHours} h`;
  return guidance.overdue ? `${head} · überfällig` : `${head} · wieder prüfen ab ${formatDate(timing.checkAgainAt)}`;
}
